import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import MandatoryField from 'modules/_shared/components/MandatoryField';
import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';
import InputNumber from 'modules/_shared/components/InputNumber';
import InputRadio from 'modules/_shared/components/InputRadio';
import InputDate from 'modules/_shared/components/InputDate';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';

import { useAddTradingOfferPage } from '../context/AddTradingOfferPageContext';

const AddTradingOfferDataSection: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useAddTradingOfferPage();

  const sharesAfterSale = data.partnerCurrentSharesCount
    ? data.partnerCurrentSharesCount - data.sharesToTransfer
    : 0;

  return (
    <div className="col-12 col-lg-8">
      <div className="card card-bordered sp-plan">
        <div className="sp-plan-info card-inner">
          <h6 className="title">{t('AvailableShares')}</h6>
        </div>

        <div className="sp-plan-desc card-inner">
          <ul className="row gx-1 text-left align-items-end">
            <li className="col-12 col-lg-6">
              <p>
                <span className="text-soft">{t('CurrentShares')}</span>
                {formatNumber(data.partnerCurrentSharesCount)}
              </p>
            </li>

            <li className="col-12 col-lg-6">
              <p>
                <span className="text-soft">{t('SharesAfterSale')}</span>
                <span className="text-left text-primary">
                  {formatNumber(sharesAfterSale)}
                </span>
              </p>
            </li>
          </ul>
        </div>
      </div>

      {!data.isPartnerView && (
        <div className="form-group mt-4">
          <label className="form-label" htmlFor="partner-select-input">
            {t('SelectPartner')} <MandatoryField />
          </label>
          <div className="form-control-wrap">
            <CustomAsyncSelect
              id="partner-select-input"
              value={data.selectedPartnerOption}
              onChange={actions.handleChangePartner}
              onFetchData={actions.handleSearchPartnerByTerm}
              placeholder={t('SearchPartner')}
              minimumInputLength={3}
              noDataLabelKey="NoPartnersHaveBeenFound"
              loadingLabelKey="LoadingPartners"
              errorLabelKey="ErrorLoadingPartners"
              defaultOptions={data.partnerOptions}
            />
          </div>
        </div>
      )}

      <div className={`form-group ${data.isPartnerView ? 'mt-4' : ''}`}>
        <label className="form-label" htmlFor="class-select-input">
          {`${t('Class')} (${t('Optional')})`}
        </label>
        <div className="form-control-wrap">
          <CustomAsyncSelect
            id="class-select-input"
            value={data.selectedHoldingClassOption}
            onChange={actions.handleChangeHoldingClass}
            onFetchData={actions.handleSearchHoldingClassByTerm}
            placeholder={t('SearchClass')}
            minimumInputLength={1}
            noDataLabelKey="NoClassesHaveBeenFound"
            loadingLabelKey="LoadingClasses"
            errorLabelKey="ErrorLoadingClasses"
            defaultOptions={data.holdingClassOptions}
          />
        </div>
      </div>

      <InputNumber
        id="shares-to-transfer-input"
        label={t('SharesToSell')}
        infoLabel={t('MaximumSharesToSell', {
          count: data.maxSharesToTransfer,
        })}
        value={data.sharesToTransfer}
        decimalScale={2}
        max={data.maxSharesToTransfer}
        onChange={actions.handleChangeSharesToTransfer}
        required
      />

      <InputCheckbox
        id="accept-partial-sale-checkbox"
        label={t('IAcceptPartialSaleOfShares')}
        checked={data.isPartialAmountEnabled}
        onChange={actions.handleChangeIsPartialAmountEnabled}
        marginBottom={3}
      />

      <div className="form-group row">
        <div className="col-6">
          <InputNumber
            id="price"
            label={t('Price')}
            infoLabel={
              data.isPricePerShare
                ? t('TradingOfferPriceInfo')
                : t('TradingOfferTotalPriceInfo')
            }
            value={data.price}
            iconRight="icon ni ni-sign-euro"
            decimalScale={2}
            onChange={actions.handleChangePrice}
            required
          />
        </div>

        <div className="col-3 d-flex align-items-center">
          <InputRadio
            id="price-per-share-radio"
            checked={data.isPricePerShare}
            label={t('PricePerShare')}
            onChange={actions.handleChangeIsPricePerShare}
          />
        </div>

        <div className="col-3 d-flex align-items-center">
          <InputRadio
            id="total-offer-price-radio"
            checked={!data.isPricePerShare}
            label={t('TotalOfferPrice')}
            onChange={actions.handleChangeIsPricePerShare}
          />
        </div>
      </div>

      <div className="row g-2">
        <div className="col-6">
          <InputDate
            id="startDate"
            label={t('StartDate')}
            value={data.startDate}
            onChange={actions.handleChangeStartDate}
            required
            lgSize
          />
        </div>

        <div className="col-6">
          <InputDate
            id="endDate"
            label={t('EndDate')}
            value={data.endDate}
            onChange={actions.handleChangeEndDate}
            required
            lgSize
          />
        </div>
      </div>
      <div className="form-note pt-1">{t('TradingOfferActiveDatesInfo')}</div>
    </div>
  );
};

export default AddTradingOfferDataSection;
